import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  siteName?: string;
  locale?: string;
  structuredData?: any;
}

const DEFAULT_SEO: Required<SEOProps> = {
  title: 'Warp Zone Gems - 游戏资源下载网站 | 高品质3A游戏、赛车、动作、冒险、解谜游戏',
  description: '专业游戏资源下载平台，提供最新3A游戏、赛车游戏、动作游戏、冒险游戏、解谜游戏等24款精品游戏免费下载，支持多种网盘下载链接，为游戏爱好者提供优质游戏体验。',
  keywords: ['游戏下载', '3A游戏', '赛车游戏', '动作游戏', '冒险游戏', '解谜游戏', '免费游戏', '游戏资源', '高品质游戏', 'PC游戏', '单机游戏', '游戏合集'],
  image: 'https://aigame.lol/icons/icon-512x512.svg',
  url: 'https://aigame.lol',
  type: 'website',
  author: 'Warp Zone Gems Team',
  publishedTime: new Date().toISOString(),
  modifiedTime: new Date().toISOString(),
  siteName: 'Warp Zone Gems',
  locale: 'zh_CN',
  structuredData: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Warp Zone Gems",
    "alternateName": "游戏资源下载网站",
    "url": "https://aigame.lol",
    "description": "专业游戏资源下载平台，提供最新3A游戏、赛车游戏、动作游戏、冒险游戏、解谜游戏等24款精品游戏免费下载",
    "inLanguage": "zh-CN",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://aigame.lol/#/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Warp Zone Gems",
      "url": "https://aigame.lol",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aigame.lol/icons/icon-512x512.svg",
        "width": 512,
        "height": 512
      }
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "游戏分类",
      "description": "5大游戏分类，24款精品游戏",
      "numberOfItems": 5,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "3A游戏",
          "description": "顶级制作水准的大作，包含赛博朋克2077、巫师3等"
        },
        {
          "@type": "ListItem", 
          "position": 2,
          "name": "赛车游戏",
          "description": "真实驾驶体验的竞速游戏，包含极限竞速、GT赛车等"
        },
        {
          "@type": "ListItem",
          "position": 3, 
          "name": "动作游戏",
          "description": "节奏紧凑的动作体验，包含战神、蜘蛛侠等"
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "冒险游戏", 
          "description": "引人入胜的冒险之旅，包含地平线、刺客信条等"
        },
        {
          "@type": "ListItem",
          "position": 5,
          "name": "解谜游戏",
          "description": "考验智慧的解谜挑战，包含传送门2、见证者等"
        }
      ]
    }
  }
};

export const SEOHead: React.FC<SEOProps> = (props) => {
  const location = useLocation();
  const seo = { ...DEFAULT_SEO, ...props };
  
  // 构建完整URL
  const fullUrl = props.url || `${DEFAULT_SEO.url}/#${location.pathname}`;
  
  // 构建完整title
  const fullTitle = props.title 
    ? `${props.title} - ${DEFAULT_SEO.siteName}`
    : DEFAULT_SEO.title;

  useEffect(() => {
    // 更新页面标题
    document.title = fullTitle;
    
    // 更新或创建meta标签
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // 基础SEO标签
    updateMetaTag('description', seo.description);
    updateMetaTag('keywords', seo.keywords.join(', '));
    updateMetaTag('author', seo.author);
    
    // Open Graph 标签
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', seo.description, true);
    updateMetaTag('og:type', seo.type, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:image', seo.image, true);
    updateMetaTag('og:site_name', seo.siteName, true);
    updateMetaTag('og:locale', seo.locale, true);
    
    // Twitter Card 标签
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', seo.description);
    updateMetaTag('twitter:image', seo.image);
    
    // 文章类型特定标签
    if (seo.type === 'article') {
      updateMetaTag('article:author', seo.author, true);
      updateMetaTag('article:published_time', seo.publishedTime, true);
      updateMetaTag('article:modified_time', seo.modifiedTime, true);
    }
    
    // 结构化数据
    if (seo.structuredData) {
      let structuredDataScript = document.getElementById('structured-data');
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script');
        structuredDataScript.id = 'structured-data';
        structuredDataScript.type = 'application/ld+json';
        document.head.appendChild(structuredDataScript);
      }
      structuredDataScript.textContent = JSON.stringify(seo.structuredData);
    }
    
    // 规范链接
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;
    
  }, [fullTitle, seo, fullUrl]);

  return null; // 这是一个无渲染组件
};

// 预设的SEO配置
export const createGameSEO = (game: {
  id: string;
  title: string;
  description?: string;
  cover_image?: string;
  tags?: string[];
  category?: string;
  published_at?: string;
  updated_at?: string;
  view_count?: number;
  download_count?: number;
  download_links?: any[];
  content?: string;
}): SEOProps => ({
  title: `${game.title} - 免费下载 | ${game.category}`,
  description: game.description || `${game.title} 免费下载，${game.category}类型游戏，支持多种网盘下载，高品质游戏体验。`,
  keywords: [
    game.title,
    game.category || '游戏',
    ...(game.tags || []),
    '免费下载',
    '游戏资源',
    'PC游戏',
    '单机游戏'
  ],
  image: game.cover_image || DEFAULT_SEO.image,
  url: `${DEFAULT_SEO.url}/#/game/${game.id}`,
  type: 'article',
  publishedTime: game.published_at,
  modifiedTime: game.updated_at,
  structuredData: {
    "@context": "https://schema.org",
    "@type": "VideoGame",
    "name": game.title,
    "alternateName": game.title,
    "description": game.description,
    "image": {
      "@type": "ImageObject",
      "url": game.cover_image,
      "caption": game.title
    },
    "genre": game.category,
    "keywords": game.tags?.join(', '),
    "datePublished": game.published_at,
    "dateModified": game.updated_at,
    "url": `${DEFAULT_SEO.url}/#/game/${game.id}`,
    "inLanguage": "zh-CN",
    "interactionStatistic": [
      {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/ViewAction",
        "userInteractionCount": game.view_count || 0
      },
      {
        "@type": "InteractionCounter", 
        "interactionType": "https://schema.org/DownloadAction",
        "userInteractionCount": game.download_count || 0
      }
    ],
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CNY",
      "availability": "https://schema.org/InStock",
      "category": "数字下载",
      "description": `${game.title} 免费下载，支持多种网盘下载方式`
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": Math.floor((game.view_count || 0) / 10),
      "bestRating": "5",
      "worstRating": "1"
    },
    "author": {
      "@type": "Organization",
      "name": "Warp Zone Gems",
      "url": "https://aigame.lol"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Warp Zone Gems",
      "url": "https://aigame.lol",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aigame.lol/icons/icon-512x512.svg"
      }
    },
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": game.title,
      "applicationCategory": "Game",
      "operatingSystem": "Windows",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "CNY"
      }
    }
  }
});

export const createCategorySEO = (category: {
  name: string;
  description?: string;
  gameCount?: number;
  games?: any[];
}): SEOProps => ({
  title: `${category.name}游戏合集 - ${category.gameCount || 0}款精品游戏免费下载`,
  description: category.description || `精选${category.name}类型游戏合集，包含${category.gameCount || 0}款高品质游戏，支持多种网盘免费下载，为${category.name}爱好者提供最佳游戏体验。`,
  keywords: [
    category.name,
    `${category.name}游戏`,
    '游戏合集',
    '免费下载',
    '游戏资源',
    'PC游戏',
    '单机游戏',
    '高品质游戏'
  ],
  type: 'website',
  url: `${DEFAULT_SEO.url}/#/categories/${encodeURIComponent(category.name)}`,
  structuredData: {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name}游戏合集`,
    "alternateName": `${category.name}游戏下载`,
    "description": category.description || `精选${category.name}类型游戏合集`,
    "url": `${DEFAULT_SEO.url}/#/categories/${encodeURIComponent(category.name)}`,
    "inLanguage": "zh-CN",
    "numberOfItems": category.gameCount || 0,
    "mainEntity": {
      "@type": "ItemList",
      "name": `${category.name}游戏列表`,
      "numberOfItems": category.gameCount || 0,
      "itemListElement": category.games?.slice(0, 10).map((game, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "VideoGame",
          "name": game.title,
          "description": game.description,
          "genre": game.category,
          "image": game.cover_image,
          "url": `${DEFAULT_SEO.url}/#/game/${game.id}`,
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "CNY",
            "availability": "https://schema.org/InStock"
          }
        }
      })) || []
    },
    "about": {
      "@type": "VideoGame",
      "genre": category.name,
      "description": `${category.name}类型的电子游戏`
    },
    "publisher": {
      "@type": "Organization",
      "name": "Warp Zone Gems",
      "url": "https://aigame.lol",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aigame.lol/icons/icon-512x512.svg"
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首页",
          "item": "https://aigame.lol"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "游戏分类",
          "item": "https://aigame.lol/#/categories"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": category.name,
          "item": `${DEFAULT_SEO.url}/#/categories/${encodeURIComponent(category.name)}`
        }
      ]
    }
  }
});